import { useState, useEffect, useMemo } from 'react';

import { useLocale } from 'next-intl';

import { useConfigValue } from '@/components/providers/config-provider';
import { useReadOnly } from '@/components/providers/read-only-provider';
import { resolveDictationKeyterms } from '@/lib/dictation/default-keyterms';
import type { SttConfig } from '@/lib/types/tenant';

import type { DictationAdapter } from '@assistant-ui/react';

const DEFAULT_TOKEN_ENDPOINT = '/api/scribe-token';

/**
 * Hook to create a dictation adapter for the current tenant.
 * Lazy loads the adapter module only when dictation is enabled.
 */
export function useDictationAdapter(): DictationAdapter | undefined {
  const isReadOnly = useReadOnly();
  const locale = useLocale();
  const sttConfig = useConfigValue<SttConfig>('meta.stt');
  const enableDictation = sttConfig?.enabled ?? false;
  const dictationProvider = sttConfig?.provider ?? 'browser';
  const tokenEndpoint = sttConfig?.scribeTokenEndpoint ?? DEFAULT_TOKEN_ENDPOINT;
  const tenantKeyterms = sttConfig?.keyterms;
  // Hint Scribe with the user's ACTIVE UI locale (not the tenant default).
  // For low-resource languages like Hebrew this materially improves
  // homophone disambiguation (e.g. "טיסה" vs "תיסע"). The hint is soft —
  // auto-detect still handles code-switching mid-utterance — so passing it
  // on multi-locale tenants is safe: a Spanish speaker on the same tenant
  // gets `languageCode='es'` for their session, not the tenant default.
  const languageHint = locale;
  // Bias on the active UI locale (not the tenant default) so a Hebrew speaker
  // on a multi-locale tenant still gets the Hebrew defaults.
  const keyterms = useMemo(
    () =>
      resolveDictationKeyterms({
        locale,
        ...(tenantKeyterms && { tenantKeyterms }),
      }),
    [locale, tenantKeyterms],
  );
  const [adapter, setAdapter] = useState<DictationAdapter | undefined>(undefined);

  useEffect(() => {
    // Read-only mode has no composer, so dictation is irrelevant. Bailing
    // here also keeps the ElevenLabs adapter chunk out of the read-only code
    // path (dynamic `import()` below is what pulls it in).
    if (isReadOnly) return;
    if (!enableDictation) return;
    if (typeof window === 'undefined') return;

    const loadAdapter = async () => {
      if (dictationProvider === 'elevenlabs') {
        const { ElevenLabsScribeAdapter } =
          await import('@/lib/dictation/elevenlabs-scribe-adapter');
        if (!ElevenLabsScribeAdapter.isSupported()) return;
        setAdapter(
          new ElevenLabsScribeAdapter({
            tokenEndpoint,
            disableInputDuringDictation: false,
            languageHint,
            ...(keyterms.length > 0 && { keyterms }),
          }),
        );
      } else {
        const { BrowserDictationAdapterWrapper } =
          await import('@/lib/dictation/browser-dictation-adapter');
        if (!BrowserDictationAdapterWrapper.isSupported()) return;
        setAdapter(
          new BrowserDictationAdapterWrapper({
            language: locale,
            continuous: true,
            interimResults: true,
          }),
        );
      }
    };

    loadAdapter();
  }, [
    isReadOnly,
    enableDictation,
    dictationProvider,
    tokenEndpoint,
    locale,
    languageHint,
    keyterms,
  ]);

  return adapter;
}
